import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { NgStyle } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, DestroyRef, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { GTM_EVENTS } from '@constants/app.constants';
import { WINDOW_LISTENER_CONSTANTS } from '@constants/window-listener.constants';
import { environment } from '@environments/environment';
import { CustomFieldModel } from '@models/custom-field.model';
import { CustomerDbModel } from '@models/customer.model';
import { PartnerDbModel, PartnerInformationBody, TokenType } from '@models/partner-db.model';
import { SurveyCustomerSmartWidgetConfigurationModel } from '@models/survey-custom-smart-widget-configuration.model';
import { SurveyStepModel } from '@models/survey-step.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { CustomEventService } from '@services/feature/custom-event.service';
import { SurveyService } from '@services/feature/survey.service';
import { WidgetService } from '@services/feature/widget.service';
import { DateUtilService } from '@services/utils/date-util.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { HelperService } from '@services/utils/helper.service';
import { LoggerService } from '@services/utils/logger.service';
import { SwRouteService } from '@services/utils/sw-route.service';
import { SurveyCustomerFormComponent } from '@survey/components/survey-customer-form/survey-customer-form.component';
import { SurveyEndStepComponent } from '@survey/components/survey-end-step/survey-end-step.component';
import { SurveyFinalStepComponent } from '@survey/components/survey-final-step/survey-final-step.component';
import { SurveyIntegrateBookingWidgetComponent } from '@survey/components/survey-integrate-booking-widget/survey-integrate-booking-widget.component';
import { SurveyQuestionsViewComponent } from '@survey/components/survey-questions-view/survey-questions-view.component';
import { GtmOperationComponent } from '@ui-lib/gtm-operation/gtm-operation.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-survey',
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss'],
  imports: [MatStepperModule, NgStyle, FormsModule, CswTextComponent, SurveyCustomerFormComponent, SurveyEndStepComponent, SurveyQuestionsViewComponent, SurveyFinalStepComponent, SurveyIntegrateBookingWidgetComponent, MatIconModule, GtmOperationComponent, TranslateModule, CalioSafePipe, CalioTranslationPipe]
})
export class SurveyComponent implements OnInit, OnDestroy {

  private destroyRef = inject(DestroyRef);

  errorMessage: string;
  showErrorMessage = false;
  showSkeleton = true;
  imagePath = environment.imageHostName;
  totalQuestionCount = 0;
  totalQuestionsAnsweredCount = 0;
  showStepperContent = false;
  showFinalStep = true;
  showBookingWidget = false;
  showCustomForm = false;
  hiddenBookingQuestions: CustomFieldModel[] = [];
  lang: string;
  partnerData: PartnerDbModel;
  isBookingCompleted = false;
  protected readonly ghostElements = new Array<number>(8);

  @ViewChild('stepper') matStepper: MatStepper;

  private customEventHandler = (event: CustomEvent) => this.onBookingCustomEvent(event);

  constructor(
    public widgetService: WidgetService,
    public surveyService: SurveyService,
    private helperService: HelperService,
    private customEventService: CustomEventService,
    private translate: TranslateService,
    private dateUtilService: DateUtilService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private ngZone: NgZone,
    private swRouteService: SwRouteService,
  ) {
    this.lang = this.translate.getFallbackLang();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(language => this.lang = language.lang);
  }

  private handleBookingCompletion(bookingData: any): void {
    if (this.isBookingCompleted) {
      LoggerService.warn('[Survey] Booking completion already handled, skipping');
      return;
    }
    LoggerService.log('[Survey] Processing booking completion');
    this.surveyService.bookingData = bookingData;
    this.isBookingCompleted = true;
    if (this.matStepper) {
      this.matStepper.next();
    }
  }

  private onBookingCustomEvent(event: CustomEvent): void {
    if (event?.detail?.eventName === WINDOW_LISTENER_CONSTANTS.APPOINTMENT_BOOKING_DONE) {
      LoggerService.log('[Survey] Received APPOINTMENT_BOOKING_DONE from web component');
      this.ngZone.run(() => {
        this.handleBookingCompletion(event.detail.bookingData);
      });
    }
  }

  ngOnInit(): void {
    this.getWidgetSteps();

    // Listen for CustomEvent from booking web component
    document.addEventListener(`${environment.bookingElementName}-event`, this.customEventHandler as EventListener);

    this.customEventService.appointmentBookingDoneEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.handleBookingCompletion(result.bookingData);
      });

    this.customEventService.progressBarCountEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.totalQuestionsAnsweredCount = result.index;
        this.sendProgressBarCount(result.isLast);
      });

    const partnerInformationBody = {
      partner_uuid: this.widgetService.partnerUUID,
      token_type: TokenType.publicSmartwidget_survey
    } as PartnerInformationBody;

    if (this.widgetService.customerConfData.uuid || this.widgetService.customerConfData.email) {
      this.widgetService.customerConfData.uuid && (partnerInformationBody.customer_uuid = this.widgetService.customerConfData.uuid);
      this.widgetService.customerConfData.email && (partnerInformationBody.customer_email = this.widgetService.customerConfData.email);
    } else {
      this.showCustomForm = true;
    }

    this.initializePartnerData(partnerInformationBody);
  }

  initializePartnerData(body: PartnerInformationBody): void {
    this.surveyService.initializePartnerData(body).subscribe({
      next: partnerData => {
        if (partnerData) {
          this.partnerData = this.surveyService.partnerData = partnerData;
          this.setCustomer(partnerData?.customer);
          this.googleAnalyticsService.partnerSubscriptionPlan = partnerData?.subscription;
          this.dateUtilService.setTimeFormatFromPartner(partnerData.widget_time_format);
        }
      },
      error: (error: HttpErrorResponse) => LoggerService.error(error)
    });
  }

  getWidgetSteps(): void {
    this.surveyService.getWidgetSteps(this.widgetService.widgetConf.context.steps)
      .pipe(finalize(() => this.showSkeleton = false))
      .subscribe({
        next: surveyStep => {
          if (surveyStep?.length > 0) {
            let totalQuestionCount = 0;
            let totalRequiredQuestionCount = 0;
            for (const survey of surveyStep) {
              if (survey.booking_questions?.length) {
                survey.booking_questions.forEach(customField => customField.is_hidden === 1 && this.hiddenBookingQuestions.push(customField));
                survey.booking_questions = survey.booking_questions.filter(customField => customField.is_hidden === 0);

                const tempFields = survey.booking_questions.filter(customField => customField.required === 1);
                totalRequiredQuestionCount = totalRequiredQuestionCount + tempFields.length;
                totalQuestionCount = totalQuestionCount + survey.booking_questions.length;
              }
            }

            this.totalQuestionCount = totalRequiredQuestionCount + 1;
            totalQuestionCount = totalQuestionCount + 1;
            // Translate any identifier-keyed prefill entries to their numeric custom-field id
            // once, using the full survey question list (visible + hidden), before consumers read.
            const allSurveyQuestions: CustomFieldModel[] = [
              ...this.hiddenBookingQuestions,
              ...surveyStep.flatMap(step => step.booking_questions ?? []),
            ];
            this.swRouteService.resolveCustomFieldIdentifiers(allSurveyQuestions);
            this.handleHiddenBookingQuestions();
            this.surveyService.surveySteps = this.helperService.sortBy(surveyStep, 'number');
          }
        },
        error: (error: HttpErrorResponse) => LoggerService.error(error)
      });
  }

  sendProgressBarCount(isLast: boolean): void {
    if (this.widgetService.clientOriginEvent) {
      this.widgetService.sendMessageToMainJs(this.widgetService.clientOriginEvent, {
        eventName: WINDOW_LISTENER_CONSTANTS.PROGRESSBAR_COUNT_EVENT,
        progressBarCount: {
          numberOfQuestions: this.totalQuestionCount,
          numberOfQuestionsAnswered: this.totalQuestionsAnsweredCount,
          isLast,
        }
      });
    }
  }

  setCustomer(customer: CustomerDbModel): void {
    if (customer) {
      this.surveyService.customerDetail = customer;
      this.showStepperContent = true;
      this.getCustomersSmartWidgetConfigurations(this.surveyService.customerDetail.id as number);
      this.handleHiddenBookingQuestions();
      this.goForward(this.matStepper, this.surveyService?.surveySteps[1]);
    } else {
      if (this.widgetService.customerConfData.uuid || this.widgetService.customerConfData.email) {
        this.showCustomForm = true;
        return;
      }

      if (
        !this.widgetService.customerConfData.email ||
        !this.widgetService.customerConfData.prename ||
        !this.widgetService.customerConfData.lastname
      ) {
        this.showCustomForm = true;
        return;
      }

      if (
        this.widgetService.customerConfData.email &&
        this.widgetService.customerConfData.prename &&
        this.widgetService.customerConfData.lastname
      ) {
        this.createCustomer();
        return;
      }
    }
  }

  createCustomer(gotoNextStep?: boolean): void {
    if (this.widgetService.widgetConf.context.customer_type_id) {
      this.widgetService.customerConfData.customer_type_id = this.widgetService.widgetConf.context.customer_type_id;
    }

    const customerConfData = new CustomerDbModel();
    customerConfData.prename = this.widgetService.customerConfData.prename;
    customerConfData.lastname = this.widgetService.customerConfData.lastname;
    customerConfData.email = this.widgetService.customerConfData.email;
    customerConfData.customer_type_id = this.widgetService.customerConfData.customer_type_id;

    if (this.widgetService.widgetConf.context?.ask_customer_city === 1) {
      customerConfData.city = this.widgetService.customerConfData.city;
    }

    if (this.widgetService.widgetConf.context?.ask_customer_zip === 1) {
      customerConfData.zip = this.widgetService.customerConfData.zip;
    }

    this.surveyService.createCustomer(customerConfData).subscribe({
      next: customer => {
        if (customer?.customer) {
          this.surveyService.customerDetail = customer?.customer;
          this.showStepperContent = true;
          this.showCustomForm = false;
          this.showErrorMessage = false;
          this.errorMessage = undefined;
          this.createCustomersSmartWidgetConfigurations(gotoNextStep);
          this.handleHiddenBookingQuestions();
        } else {
          this.errorMessage = 'survey_component.customer_addition_failed';
          this.showErrorMessage = true;
        }
      },
      error: (error: HttpErrorResponse) => {
        LoggerService.error(error);
        this.errorMessage = 'survey_component.customer_addition_failed';
        this.showErrorMessage = true;
      }
    });
  }

  onDone(): void {
    this.surveyService.calculateSurveyProgressBarCount(true);
  }

  onEndProcessEvent(): void {
    this.onDone();
  }

  onBookAppointmentEvent(): void {
    this.showFinalStep = false;
    this.showBookingWidget = true;
  }

  onStepChanged(event: StepperSelectionEvent): void {
    LoggerService.log('Inside on step changed ', event);
    if (this.surveyService.surveySteps?.length > 0) {
      LoggerService.log('Inside on step changed 1 ', event);
      if (this.surveyService.surveyCustomerSWconfigurationData) {
        LoggerService.log('Inside on step changed 2 ', event);
        if (event.selectedIndex === (this.surveyService.surveySteps?.length - 1)) {
          LoggerService.log('Last Step Inside on step changed 3 ', event);
          this.updateCustomersSmartWidgetConfigurations(true, this.surveyService.surveySteps[event.selectedIndex].id);
        } else {
          LoggerService.log('Other steps Inside on step changed 4 ', event);
          this.updateCustomersSmartWidgetConfigurations(false, this.surveyService.surveySteps[event.selectedIndex].id);
        }

        this.fireGoogleEvent(event.selectedIndex);

      } else {
        LoggerService.log('Inside on step changed 5 ', event);
        // this.createCustomersSmartWidgetConfigurations();
      }
    } else {
      LoggerService.log('Empty this.surveyService.surveySteps ', this.surveyService.surveySteps);
    }
  }

  goBack(stepper: MatStepper): void {
    stepper.previous();
  }

  goForward(stepper: MatStepper, surveyStep: SurveyStepModel): void {
    const endOfProcessSurvey = this.widgetService.endOfProcessSurvey;
    const endOfProcessSurveyStatus = (endOfProcessSurvey?.status ? endOfProcessSurvey.status : false);
    const endOfProcessSurveyStep = (endOfProcessSurvey?.step ? endOfProcessSurvey.step : 0);

    if (surveyStep?.is_start === 1 && this.showCustomForm) {
      this.createCustomer(true);
    } else {
      (!(endOfProcessSurveyStatus && endOfProcessSurveyStep === surveyStep.step_number)) && stepper.next();
    }
  }

  handleNextButtonState(surveyStep: SurveyStepModel, formStatus: boolean): boolean {
    if (surveyStep.step_number === 1) {
      return formStatus;
    } else {
      const endOfProcessSurvey = this.widgetService.endOfProcessSurvey;
      const endOfProcessSurveyStatus = (endOfProcessSurvey?.status ? endOfProcessSurvey.status : false);
      const endOfProcessSurveyStep = (endOfProcessSurvey?.step ? endOfProcessSurvey.step : 0);
      return (endOfProcessSurveyStatus === true && endOfProcessSurveyStep === surveyStep.step_number) && formStatus === false;
    }
  }

  goBackward(stepper: MatStepper): void {
    stepper.previous();
  }

  getCustomersSmartWidgetConfigurations(customerId: number): void {
    if (!customerId) {
      LoggerService.warn('Customer Id is not defined');
      return;
    }
    this.surveyService
      .getCustomersSmartWidgetConfigurations(
        customerId,
        this.widgetService.widgetConf.widget_uuid
      ).subscribe({
        next: result => {
          LoggerService.log('result is ', result);
          if (result?.length > 0) {
            this.surveyService.surveyCustomerSWconfigurationData = result[0];
          } else {
            this.createCustomersSmartWidgetConfigurations(false);
          }
        },
        error: (error: HttpErrorResponse) => LoggerService.error(error)
      });
  }

  createCustomersSmartWidgetConfigurations(gotoNextStep: boolean): void {
    const body: SurveyCustomerSmartWidgetConfigurationModel = new SurveyCustomerSmartWidgetConfigurationModel();
    body.start = this.dateUtilService.getFormattedDateByFormat(new Date(), 'YYYY-MM-DD HH:mm:ss');
    body.end = null;
    body.customer_id = this.surveyService.customerDetail.id as number;
    body.widget_uuid = this.widgetService.widgetConf.widget_uuid;
    body.partner_id = this.widgetService.widgetConf.partner.id;
    body.widget_step_id = this.surveyService?.surveySteps?.length > 0 ? this.surveyService?.surveySteps[0].id : null;

    this.surveyService.createCustomersSmartWidgetConfigurations(body).subscribe({
      next: result => {
        this.surveyService.surveyCustomerSWconfigurationData = result;
        if (gotoNextStep) {
          this.matStepper.next();
        }
      },
      error: (error: HttpErrorResponse) => LoggerService.error(error)
    });
  }

  updateCustomersSmartWidgetConfigurations(isLastStep: boolean, stepId: number): void {
    LoggerService.log('Inside updateCustomersSmartWidgetConfigurations');
    const body: SurveyCustomerSmartWidgetConfigurationModel = new SurveyCustomerSmartWidgetConfigurationModel();
    if (isLastStep) {
      body.end = this.dateUtilService.getFormattedDateByFormat(new Date(), 'YYYY-MM-DD HH:mm:ss');
    }
    body.customer_id = this.surveyService.customerDetail.id as number;
    body.widget_uuid = this.widgetService.widgetConf.widget_uuid;
    body.partner_id = this.widgetService.widgetConf.partner.id;
    body.widget_step_id = stepId;

    this.surveyService.updateCustomersSmartWidgetConfigurations(body, this.surveyService.surveyCustomerSWconfigurationData.id).subscribe({
      next: result => this.surveyService.surveyCustomerSWconfigurationData = result,
      error: (error: HttpErrorResponse) => LoggerService.error(error)
    });
  }

  handleHiddenBookingQuestions(): void {
    LoggerService.warn('Inside handleHiddenBookingQuestions() this.hiddenBookingQuestions', this.hiddenBookingQuestions);
    this.surveyService.handleHiddenBookingQuestions(
      this.hiddenBookingQuestions,
      this.surveyService.customerDetail
    );
  }

  fireGoogleEvent(index: number): void {
    if (this.surveyService.surveySteps?.length > 0) {
      if (index === 0) {
        this.googleAnalyticsService.emitSurveyEvent({
          eventStep: 1,
          eventName: GTM_EVENTS.survey_start_step,
          stepName: this.surveyService.surveySteps[0].title
        });
      } else if (index === (this.surveyService.surveySteps?.length - 1)) {
        this.googleAnalyticsService.emitSurveyEvent({
          eventStep: index + 1,
          eventName: GTM_EVENTS.survey_last_step,
          stepName: this.surveyService.surveySteps[this.surveyService.surveySteps?.length - 1].title
        });
        this.googleAnalyticsService.generateSessionUuid();
      } else {
        this.googleAnalyticsService.emitSurveyEvent({
          eventStep: index + 1,
          eventName: GTM_EVENTS.other_survey_steps + (index + 1),
          stepName: this.surveyService.surveySteps[index].title
        });
      }
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener(`${environment.bookingElementName}-event`, this.customEventHandler as EventListener);
  }
}
